import type { QuestionBrowseTaxonomy, QuestionPurpose } from '@course-authoring/shared-types';
import { QUESTION_BANK_BROWSE_COPY as COPY } from '../../components/questionBankBrowse/questionBankBrowseCopy';
import type { QuestionBankBrowseStyles } from '../../components/questionBankBrowse/questionBankBrowseStyles';
import { QuestionBankTaxonomySections } from '../../components/questionBankBrowse/QuestionBankTaxonomySections';

export interface QuestionBankTaxonomyPanelProps {
  styles: QuestionBankBrowseStyles;
  /** The scope's taxonomies from the facets; empty until they load. */
  taxonomies: readonly QuestionBrowseTaxonomy[];
  /** The purpose the list is filtered by; only Sentiment questions carry framework tags. */
  purpose: QuestionPurpose;
  checkedLabelIds: readonly number[];
  openGroups: ReadonlySet<string>;
  /** Opens or closes these parent groups. */
  onSetGroups: (keys: readonly string[], open: boolean) => void;
  onToggleLabel: (labelId: number) => void;
  onShowSentiment: () => void;
}

/**
 * The page's taxonomy panel: the intro line over the shared framework
 * sections (Risk Areas, Leadership Skills, Culture Skills) and, when Purpose
 * is not Sentiment, an explanation with "Show Sentiment questions". Owns no
 * state.
 */
export function QuestionBankTaxonomyPanel({
  styles,
  taxonomies,
  purpose,
  checkedLabelIds,
  openGroups,
  onSetGroups,
  onToggleLabel,
  onShowSentiment,
}: QuestionBankTaxonomyPanelProps) {
  const b = styles.browse;

  if (purpose !== 'Sentiment') {
    return (
      <aside aria-label={COPY.panelLabel} style={b.panel} data-testid="taxonomy-panel">
        <p style={b.panelPlaceholderTitle}>{COPY.panelPlaceholderTitle}</p>
        <p style={b.panelPlaceholderBody}>{COPY.panelPlaceholderBody(purpose)}</p>
        <button type="button" style={b.panelLink} onClick={onShowSentiment}>{COPY.showSentiment}</button>
      </aside>
    );
  }

  return (
    <aside aria-label={COPY.panelLabel} style={b.panel} data-testid="taxonomy-panel">
      <p style={b.panelIntro}>{COPY.panelIntro}</p>
      <QuestionBankTaxonomySections
        styles={styles}
        taxonomies={taxonomies}
        checkedLabelIds={checkedLabelIds}
        openGroups={openGroups}
        onSetGroups={onSetGroups}
        onToggleLabel={onToggleLabel}
      />
    </aside>
  );
}
